import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MapPin, Calendar, User, DollarSign, Trash2, Image as ImageIcon, Save, Receipt, FileText, CreditCard } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import MarketingAutomationPanel from "@/components/MarketingAutomationPanel";
import SocialPostSection from "@/components/SocialPostSection";
import JobCostSheet from "@/components/JobCostSheet";
import LinkedEstimateCard from "@/components/LinkedEstimateCard";
import OnMyWayButtons from "@/components/OnMyWayButtons";
import EmailComposeDialog from "@/components/EmailComposeDialog";
import CustomerCommsPanel from "@/components/CustomerCommsPanel";
import EmailThread from "@/components/EmailThread";

const STATUSES = ["scheduled", "in_progress", "completed", "canceled"];

const STATUS_TONE = {
  draft: "bg-zinc-100 text-zinc-700", sent: "bg-blue-50 text-blue-700",
  viewed: "bg-amber-50 text-amber-700", accepted: "bg-emerald-50 text-emerald-700",
  declined: "bg-rose-50 text-rose-700", expired: "bg-zinc-100 text-zinc-500",
  scheduled: "bg-emerald-50 text-emerald-700", converted: "bg-emerald-50 text-emerald-700",
};

const fmtEstDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};

export default function JobProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [business, setBusiness] = useState(null);
  // Lightweight estimate summary — powers the Snapshot metadata (number,
  // status, created date). LinkedEstimateCard fetches its own full copy
  // separately; the small duplicate GET keeps the card self-contained.
  const [estimateSummary, setEstimateSummary] = useState(null);
  const [newPhoto, setNewPhoto] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: biz }] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/business`),
      ]);
      setJob(data);
      setBusiness(biz);
      if (data.customer_id) {
        const { data: c } = await api.get(`/customers/${data.customer_id}`);
        setCustomer(c);
      }
      // Fetch estimate metadata for Snapshot display when the job was
      // created from an estimate. 404 (deleted or cross-tenant) silently
      // leaves the section hidden.
      if (data.estimate_id) {
        try {
          const { data: est } = await api.get(`/estimates/${data.estimate_id}`);
          setEstimateSummary({
            estimate_number: est.estimate_number,
            status: est.status,
            created_at: est.created_at,
          });
        } catch { setEstimateSummary(null); }
      } else {
        setEstimateSummary(null);
      }
    } catch { toast.error("Not found"); nav("/app/jobs"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/jobs/${id}`, {
        customer_id: job.customer_id,
        estimate_id: job.estimate_id,
        lead_id: job.lead_id,
        title: job.title,
        service_address: job.service_address,
        scheduled_start: job.scheduled_start,
        scheduled_end: job.scheduled_end,
        value: Number(job.value) || 0,
        assigned_to: job.assigned_to,
        notes: job.notes,
        photos: job.photos || [],
        status: job.status,
      });
      setJob(data);
      toast.success("Saved");
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  const setStatus = async (s) => {
    setJob((j) => ({ ...j, status: s }));
    try {
      await api.patch(`/jobs/${id}`, { status: s });
      toast.success(`Status: ${s.replace(/_/g," ")}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const addPhoto = () => {
    if (!newPhoto.trim()) return;
    setJob((j) => ({ ...j, photos: [...(j.photos||[]), newPhoto.trim()] }));
    setNewPhoto("");
  };
  const rmPhoto = (i) => setJob((j) => ({ ...j, photos: j.photos.filter((_, idx) => idx !== i) }));

  const remove = async () => {
    if (!window.confirm("Delete this job?")) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Deleted"); nav("/app/jobs"); } catch { toast.error("Failed"); }
  };

  if (!job) return <div className="p-8 text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-6xl">
      <button onClick={()=>nav("/app/jobs")} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="job-back-btn">
        <ArrowLeft className="w-4 h-4"/> All jobs
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Job</div>
          <Input value={job.title} onChange={(e)=>setJob({...job, title: e.target.value})} className="!text-4xl !font-heading !font-bold !h-auto !py-1 !px-0 !border-0 !shadow-none focus-visible:!ring-0" data-testid="job-title-input"/>
          {customer && (
            <div className="mt-1 text-sm text-zinc-600 flex items-center gap-2">
              <User className="w-3.5 h-3.5"/>
              <button onClick={()=>nav(`/app/customers/${customer.id}`)} className="hover:text-[#FF5A00] hover:underline">{customer.name}</button>
              {customer.phone && <span className="text-zinc-400">· {customer.phone}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={job.status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="job-status-select"><SelectValue/></SelectTrigger>
            <SelectContent>{STATUSES.map((s)=><SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g," ")}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={save} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="job-save-btn">
            <Save className="w-4 h-4 mr-1"/> {saving ? "Saving…" : "Save"}
          </Button>
          {/* View Estimate — rendered as a real anchor so Ctrl/Cmd/middle-click
              opens the estimate in a new tab. Hidden when the job has no
              estimate_id (no disabled state per spec). */}
          {job.estimate_id && (
            <a
              href={`/app/estimates/${job.estimate_id}`}
              onClick={(e) => {
                // Left-click without modifier → SPA navigation, no full reload.
                if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                  e.preventDefault();
                  nav(`/app/estimates/${job.estimate_id}`);
                }
              }}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md h-9 px-4 border border-input bg-background hover:bg-accent hover:text-accent-foreground text-sm font-medium no-underline"
              data-testid="job-view-estimate-btn"
            >
              <FileText className="w-4 h-4 mr-1"/> View Estimate
            </a>
          )}
          <Button variant="outline" onClick={async ()=>{
            try { const { data } = await api.post(`/invoices/from-job/${id}`); toast.success(`Created ${data.invoice_number}`); nav(`/app/invoices/${data.id}`); }
            catch { toast.error("Failed"); }
          }} data-testid="job-invoice-btn"><Receipt className="w-4 h-4 mr-1"/> Invoice</Button>
          {/* Collect Payment — shown only when the job already has an invoice.
              Deep-links to InvoiceProfile where RecordPaymentDialog lives. */}
          {job.invoice_id && (
            <a
              href={`/app/invoices/${job.invoice_id}?action=collect`}
              onClick={(e) => {
                if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                  e.preventDefault();
                  nav(`/app/invoices/${job.invoice_id}?action=collect`);
                }
              }}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium no-underline btn-lift"
              data-testid="job-collect-payment-btn"
            >
              <CreditCard className="w-4 h-4 mr-1"/> Collect Payment
            </a>
          )}
          <Button variant="outline" onClick={remove} className="text-red-600" data-testid="job-delete-btn"><Trash2 className="w-4 h-4"/></Button>
        </div>
      </div>

      {/* Phase 4 — On-My-Way quick texts, directly under the action bar */}
      {job.status !== "completed" && job.status !== "canceled" && job.customer_id && (
        <div className="mb-6 flex items-center gap-3 flex-wrap" data-testid="job-detail-on-my-way-row">
          <span className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500">Quick text</span>
          <OnMyWayButtons job={job} size="md" />
          {job.customer_id && (
            <CustomerCommsPanel customerId={job.customer_id} triggerLabel="Open SMS thread" />
          )}
          {customer?.email && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEmailOpen(true)}
              className="border-sky-200 text-sky-700 hover:bg-sky-50"
              data-testid="job-email-btn"
            >
              <FileText className="w-4 h-4 mr-1"/> Email customer
            </Button>
          )}
        </div>
      )}

      <EmailComposeDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        customerId={job.customer_id}
        jobId={job.id || job._id}
        prefillTo={customer?.email || ""}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Details */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp space-y-4">
            <h2 className="font-heading text-xl font-bold">Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Service address</Label>
                <Input value={job.service_address||""} onChange={(e)=>setJob({...job, service_address: e.target.value})} data-testid="job-address-input"/>
              </div>
              <div>
                <Label>Scheduled start</Label>
                <Input type="datetime-local" value={(job.scheduled_start||"").slice(0,16)} onChange={(e)=>setJob({...job, scheduled_start: e.target.value})}/>
              </div>
              <div>
                <Label>Scheduled end</Label>
                <Input type="datetime-local" value={(job.scheduled_end||"").slice(0,16)} onChange={(e)=>setJob({...job, scheduled_end: e.target.value})}/>
              </div>
              <div>
                <Label>Value ($)</Label>
                <Input type="number" step="0.01" value={job.value} onChange={(e)=>setJob({...job, value: e.target.value})} data-testid="job-value-input"/>
              </div>
              <div>
                <Label>Assigned to</Label>
                <Input value={job.assigned_to||""} onChange={(e)=>setJob({...job, assigned_to: e.target.value})} placeholder="Team member name"/>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea rows={3} value={job.notes||""} onChange={(e)=>setJob({...job, notes: e.target.value})}/>
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon className="w-5 h-5 text-[#FF5A00]"/>
              <h2 className="font-heading text-xl font-bold">Photos</h2>
              <span className="ml-auto text-xs text-zinc-500 font-mono-accent">{(job.photos||[]).length}</span>
            </div>
            <div className="flex gap-2 mb-3">
              <Input value={newPhoto} onChange={(e)=>setNewPhoto(e.target.value)} placeholder="Paste an image URL" data-testid="job-photo-input"/>
              <Button variant="outline" onClick={addPhoto} data-testid="job-add-photo-btn">Add</Button>
            </div>
            {(job.photos||[]).length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {job.photos.map((p, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-zinc-200 group">
                    <img src={p} alt="" className="w-full h-full object-cover"/>
                    <button onClick={()=>rmPhoto(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100">
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </div>
                ))}
              </div>
            ) : <div className="text-sm text-zinc-500">No photos yet.</div>}
          </div>

          {/* Post-Job Marketing Automation (Iter43 Phase 1) */}
          <MarketingAutomationPanel job={job} business={business} onJobRefresh={load} />

          {/* Bug-fix — Linked estimate card. Renders only when the job carries
              an estimate_id (i.e. was created via /api/estimates/{id}/convert-to-job
              or /api/jobs with estimate_id). Preserves the
              Customer → Estimate → Job relationship so scheduled/converted
              estimates remain visible on the job page. */}
          <LinkedEstimateCard job={job} />

          {/* Iter P2.1a — Job Costing (opt-in per business; component
              short-circuits to null when the flag is off or the caller
              lacks admin+). */}
          <JobCostSheet jobId={id} jobHasEstimate={!!job.estimate_id} />

          {/* AI Social Post (Iter43 Phase 2) */}
          <SocialPostSection job={job} business={business} />

          {/* Phase 2 — Email history. Defaults to the "This job" view
              (toggle to Customer thread for the full history) so replies
              land where the work lives. */}
          {job.customer_id && (
            <EmailThread
              customerId={job.customer_id}
              jobId={job.id || job._id}
              prefillTo={customer?.email || ""}
              title="Email history"
            />
          )}
        </div>

        {/* Right */}
        <div className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
            <h2 className="font-heading text-xl font-bold mb-4">Snapshot</h2>
            <div className="space-y-3 text-sm">
              <Row icon={DollarSign} label="Value" value={`$${(Number(job.value)||0).toLocaleString()}`}/>
              <Row icon={Calendar} label="Starts" value={job.scheduled_start ? new Date(job.scheduled_start).toLocaleString() : "—"}/>
              <Row icon={MapPin} label="Address" value={job.service_address || "—"}/>
              <Row icon={User} label="Assigned" value={job.assigned_to || "—"}/>
            </div>
            {job.estimate_id && (
              <div className="mt-4 pt-4 border-t border-zinc-100 space-y-2" data-testid="job-snapshot-estimate">
                {estimateSummary && (
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between gap-2">
                      <span className="text-zinc-500 uppercase tracking-widest font-mono">Estimate #</span>
                      <span className="font-mono text-[#FF5A00] font-semibold" data-testid="job-snapshot-estimate-number">
                        {estimateSummary.estimate_number || job.estimate_id.slice(0, 8)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-zinc-500 uppercase tracking-widest font-mono">Status</span>
                      <span
                        className={`px-2 py-0.5 rounded uppercase tracking-widest font-mono ${STATUS_TONE[estimateSummary.status] || "bg-zinc-100 text-zinc-700"}`}
                        data-testid="job-snapshot-estimate-status"
                      >
                        {estimateSummary.status || "draft"}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-zinc-500 uppercase tracking-widest font-mono">Created</span>
                      <span className="text-zinc-700" data-testid="job-snapshot-estimate-date">
                        {fmtEstDate(estimateSummary.created_at)}
                      </span>
                    </div>
                  </div>
                )}
                <a
                  href={`/app/estimates/${job.estimate_id}`}
                  onClick={(e) => {
                    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                      e.preventDefault();
                      nav(`/app/estimates/${job.estimate_id}`);
                    }
                  }}
                  className="text-sm text-[#FF5A00] hover:underline inline-block"
                  data-testid="job-snapshot-open-estimate-link"
                >
                  Open Original Estimate →
                </a>
              </div>
            )}
          </div>
          <FileUploader resourceType="job" resourceId={id} title="Job photos & files"/>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-md bg-zinc-100 flex items-center justify-center shrink-0"><Icon className="w-3.5 h-3.5 text-zinc-600"/></div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
        <div className="text-sm text-zinc-900 font-medium truncate">{value}</div>
      </div>
    </div>
  );
}
